import React, { useState, useEffect } from 'react';
import qs from 'qs';
import moment from 'moment';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Button,
  Divider,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Checkbox,
  ListItemText,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  Input
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import styled from 'styled-components';
import { getReceptions } from '../receptionsActions';
import PeriodicityButton from './PeriocidityButton';
import { receptionFilters } from '../../commands/search/filters';

const CustomDivider = styled(Divider)`
  width: 1px;
  height: 40px;
  margin: 4px;
`;

const CustomPaper = styled(Paper)`
  padding: 2px 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/**
 * Barre de recherche
 * @param {Function} setQuery    Mettre à jour les query de la requête
 * @param {String}   initParams  Query d'initialisation
 */
const SearchReceptions = ({ dispatchGetReceptions }) => {
  const history = useHistory();
  const { search: locationSearch } = useLocation();
  const [init, setInit] = useState(false);
  const defaultParams = {
    page: 1,
    query: '',
    maxResults: 8
  };

  const getUrlParams = () =>
    qs.parse(locationSearch, { ignoreQueryPrefix: true });

  /**
   * Initialisation de l'url
   */
  useEffect(() => {
    const params = {
      ...defaultParams,
      ...getUrlParams()
    };
    history.replace({
      search: qs.stringify(params)
    });
    setInit(true);
  }, []);

  useEffect(() => {
    if (!init) {
      return;
    }
    const currentParams = getUrlParams();
    const params = {
      ...currentParams,
      statesCodes: currentParams.statesCodes
        ? currentParams.statesCodes.join(',')
        : undefined
    };
    dispatchGetReceptions(params);
  }, [getUrlParams().page, dispatchGetReceptions, init]);

  /**
   * Modification de la donnée inputText utiliser
   * dans la query. Pour rechercher dans les commandes
   * @param  {Object} event event
   */
  const handleChange = event => {
    history.replace({
      search: qs.stringify({
        ...getUrlParams(),
        query: event.target.value
      })
    });
  };

  /**
   * Function exécuté lors du clique sur l'icone de recherche
   */
  const search = () => {
    const urlParams = getUrlParams();
    const params = {
      ...urlParams,
      statesCodes: urlParams.statesCodes
        ? urlParams.statesCodes.join(',')
        : undefined
    };

    history.replace({
      search: qs.stringify({
        ...getUrlParams(),
        page: 1
      })
    });
    dispatchGetReceptions(params);
  };

  /**
   * Function exécuté lors du clique sur la touche Enter
   */
  const keyPressed = event => {
    if (event.key === 'Enter') {
      search();
    }
  };

  /**
   * Function permettant de reinitialiser tous les filtres de recherche,
   * elle remet aussi la pagination du tableau à 0
   */
  const reset = () => {
    history.replace({
      search: qs.stringify(defaultParams)
    });
    dispatchGetReceptions(defaultParams);
  };

  const handleChangeFilter = event => {
    const { value } = event.target;
    history.replace({
      search: qs.stringify({
        ...getUrlParams(),
        statesCodes: value,
        page: 1
      })
    });
    const currentParams = getUrlParams();
    const params = {
      ...currentParams,
      statesCodes: value.join(',')
    };
    dispatchGetReceptions(params);
  };

  return (
    <CustomPaper>
      <FormControl style={{ margin: '5px 0px 5px 50px', width: '20%' }}>
        <InputLabel htmlFor="select-multiple-checkbox">
          État des réceptions
        </InputLabel>
        <Select
          multiple
          style={{ width: 220 }}
          value={
            qs.parse(locationSearch, { ignoreQueryPrefix: true }).statesCodes ||
            []
          }
          onChange={handleChangeFilter}
          input={<Input id="select-multiple-checkbox" />}
          renderValue={selected =>
            selected
              .map(
                item =>
                  receptionFilters.find(element => element.value === item).label
              )
              .join(', ')
          }
        >
          {receptionFilters
            .filter(h => !h.hidden)
            .map(item => (
              <MenuItem key={item.label} value={item.value}>
                <Checkbox
                  checked={
                    qs.parse(locationSearch, { ignoreQueryPrefix: true })
                      .statesCodes
                      ? qs
                          .parse(locationSearch, { ignoreQueryPrefix: true })
                          .statesCodes.indexOf(item.value) > -1
                      : false
                  }
                />
                <ListItemText primary={item.label} />
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <TextField
        style={{ width: '50%', marginRight: 30, marginLeft: 20 }}
        id="name"
        label="Recherche"
        value={qs.parse(locationSearch, { ignoreQueryPrefix: true }).query}
        onChange={handleChange}
        onKeyPress={keyPressed}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label="Search" onClick={search}>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      <PeriodicityButton
        initButtonLabelCustom={
          getUrlParams().expectedDeliveryDateFrom
            ? `${moment(getUrlParams().expectedDeliveryDateFrom)
                .add(3, 'hours')
                .format('DD/MM/YYYY')} -
          ${moment(getUrlParams().expectedDeliveryDateTo)
            .add(3, 'hours')
            .format('DD/MM/YYYY')}`
            : 'Filtrer par date'
        }
        defaultPeriodicity="CUSTOM_RANGE"
        onChange={({ dateFrom, dateTo }) => {
          const currentParams = getUrlParams();
          const params = {
            ...currentParams,
            expectedDeliveryDateFrom: moment(dateFrom).format('YYYY-MM-DD'),
            expectedDeliveryDateTo: moment(dateTo).format('YYYY-MM-DD'),
            page: 1
          };
          history.replace({
            search: qs.stringify(params)
          });
          dispatchGetReceptions({
            ...params,
            statesCodes: params.statesCodes
              ? params.statesCodes.join(',')
              : undefined
          });
        }}
      />
      <CustomDivider />
      <Button
        color="primary"
        variant="contained"
        disabled={'query' === ''}
        style={{ marginRight: 30, marginLeft: 30 }}
        onClick={reset}
      >
        Réinitialiser
      </Button>
    </CustomPaper>
  );
};

const mapDispatchToProps = dispatch => ({
  dispatchGetReceptions: params => dispatch(getReceptions(params))
});

SearchReceptions.propTypes = {
  dispatchGetReceptions: PropTypes.func.isRequired
};

export default connect(
  null,
  mapDispatchToProps
)(SearchReceptions);
